// Channel inventory - shared between ChannelStatusPanel, AccountChannelsSection
// and the Staging Area's channel selector (WP-CH-02).
//
// Each entry carries:
//   id     - the inventory's own identifier (kept stable for existing callers)
//   token  - the channel name ChannelGate speaks. The inventory ids and the
//            backend names drifted ('google_chat' here, 'googlechat' there);
//            this field confines that translation to this one file. The
//            selector displays `name` and sends `token`.
//   active - whether the channel appears in the selector and status panels.
//
// This catalogue matches the customer Channels surface. Organization
// enablement decides production selection; no slot is hidden here.
export const CHANNELS = [
    { id: 'browser', name: 'Browser', token: 'browser', bindingToken: 'email', description: 'Survey runs in the browser; invitations are sent by email.' },
    { id: 'whatsapp', name: 'WhatsApp', token: 'whatsapp' },
    { id: 'messenger', name: 'Facebook Messenger', token: 'messenger' },
    { id: 'sms', name: 'SMS', token: 'sms' },
    { id: 'rcs', name: 'RCS', token: 'rcs' },
    { id: 'google_chat', name: 'Google Chat', token: 'googlechat' },
    { id: 'microsoft_teams', name: 'Microsoft Teams', token: 'msteams' },
    { id: 'slack', name: 'Slack', token: 'slack' },
];

// DEFAULT_CHANNEL is what every account gets until an author picks otherwise:
// the organization-controlled Email invitation channel.
export const DEFAULT_CHANNEL = 'browser';

// The selector shows every channel; durable organization enablement decides
// which options admit a new production send.
export const selectableChannels = () => CHANNELS;

// CRM choices come from the pinned CRM capability profile. The survey's
// designed-channel list governs direct sends; it must not silently remove a
// recipient-selected CRM route.
export const selectableCRMChannels = (_designedChannels = [], capabilities = []) => {
    const capabilityByChannel = new Map((capabilities || []).map((item) => [
        canonicalExecutionChannel(item.channel),
        item,
    ]));
    return CHANNELS.filter((channel) => {
        const capability = capabilityByChannel.get(channel.token);
        return capability?.supports_crm === true
            && capability?.crm_supports_state_round_trip === true;
    });
};

// An empty stored list means "all eligible CRM channels" in DBAM.
// Reflect that same meaning in the checkboxes so the screen and dispatch
// preview cannot disagree about what the recipient will see.
export const effectiveCRMChannels = (storedChannels = [], eligibleChannels = []) => {
    const eligible = eligibleChannels.map((channel) => channel.token);
    const eligibleSet = new Set(eligible);
    const stored = [...new Set((storedChannels || []).map(canonicalExecutionChannel))]
        .filter((channel) => eligibleSet.has(channel));
    return stored.length > 0 ? stored : eligible;
};

// channelLabel translates a backend token into the display name.
export const channelLabel = (token) => {
    const hit = CHANNELS.find(c => c.token === token);
    if (hit) return hit.name;
    if (token === 'browser' || token === 'web') return 'Browser';
    if (token === 'channel_response') return 'Channel Request Method';
    if (token === 'email') return 'Email';
    return token || '';
};

export const canonicalExecutionChannel = (token) => {
    if (token === 'email' || token === 'web') return 'browser';
    if (token === 'google_chat') return 'googlechat';
    if (token === 'microsoft_teams' || token === 'teams') return 'msteams';
    return token || '';
};
